import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import {
  Typography,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Skeleton,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendUp,
  faBullhorn,
  faMagnifyingGlass,
} from "@fortawesome/pro-duotone-svg-icons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsSankey from "highcharts/modules/sankey";
import HighchartsExporting from "highcharts/modules/exporting";
import ErrorBoundary from "../../../ErrorBoundry";
import { advanceSearch } from "../../../_helper/services/advanceSearchv2";
import { ShowError } from "../../../_helper/helper";
import { getFormalApiErrorMessage } from "../../../utils/apiError";

HighchartsSankey(Highcharts);
HighchartsExporting(Highcharts);

const renderValue = (value) =>
  value !== null && value !== undefined && value !== "" ? value : "N/A";

// Fixed pipeline order + color per stage, matching StageOrder 1-4 from
// dbo.fn_AnnouncementOnGoingStatusTracker(): Evaluation -> Eligibility ->
// General Availability -> Certification.
const STAGE_ORDER = ["Evaluation", "Eligibility", "General Availability", "Certification"];
const STAGE_COLOR = {
  Evaluation: "#6c757d",
  Eligibility: "#0d6efd",
  "General Availability": "#8540f5",
  Certification: "#198754",
};

const getStatusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("deleted")) return "#dc3545";
  if (s.includes("completed") || s.includes("interview result")) return "#198754";
  if (s.includes("pending")) return "#fd7e14";
  return "#6c757d";
};

// Aging color for DaysPending - how long a record has sat with no activity.
// Thresholds are a starting point; adjust once real usage patterns are known.
const getAgingColor = (days) => {
  if (days === null || days === undefined) return "#6c757d";
  if (days <= 3) return "#198754";
  if (days <= 10) return "#fd7e14";
  return "#dc3545";
};

// The 7 real, confirmed Certification steps (matches CertificationStepIndex
// from the SQL function, which uses the same conditions as the app's own
// CertificationStatusStepper.jsx). Nothing beyond "Interview Result" is
// tracked anywhere in the system, so the trail intentionally ends there.
const CERTIFICATION_STEPS = [
  "Created",
  "AD Approved",
  "POC Approved",
  "EEO/Personnel Assigned",
  "EEO/Personnel Approved",
  "Certificate Sent",
  "Interview Result",
];

// Horizontal step trail for a Certification record - green for completed
// steps, highlighted for the one currently being worked on, grey for
// steps not reached yet. stepIndex === 6 means every step is done.
const CertificationStepper = ({ stepIndex }) => {
  if (stepIndex === null || stepIndex === undefined) return null;
  return (
    <div className="d-flex align-items-center flex-wrap gap-1">
      {CERTIFICATION_STEPS.map((label, idx) => {
        const isDone = stepIndex === 6 ? idx <= 6 : idx < stepIndex;
        const isCurrent = stepIndex !== 6 && idx === stepIndex;
        return (
          <Fragment key={label}>
            <Chip
              size="small"
              label={label}
              sx={{
                color: "white",
                background: isDone ? "#198754" : isCurrent ? "#fd7e14" : "#adb5bd",
                fontWeight: isCurrent ? "bold" : "normal",
              }}
            />
            {idx < CERTIFICATION_STEPS.length - 1 && (
              <Typography component="span" sx={{ color: "#adb5bd" }}>
                &rarr;
              </Typography>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

// Horizontal trail of the actual remarks logged over time for an
// Eligibility/General Availability/Evaluation record (no structured
// workflow columns exist for these stages, so this is the only honest
// "step by step" view - it's whatever was really logged, in order).
const RemarksTrail = ({ trail }) => {
  if (!trail) return null;
  const steps = trail.split(" -> ").filter(Boolean);
  if (steps.length === 0) return null;
  return (
    <div className="d-flex align-items-center flex-wrap gap-1">
      {steps.map((step, idx) => (
        <Fragment key={idx}>
          <Chip size="small" label={step} variant="outlined" />
          {idx < steps.length - 1 && (
            <Typography component="span" sx={{ color: "#adb5bd" }}>
              &rarr;
            </Typography>
          )}
        </Fragment>
      ))}
    </div>
  );
};

function AnnouncementStatusDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [announcementIdInput, setAnnouncementIdInput] = useState("");
  const [appliedAnnouncementId, setAppliedAnnouncementId] = useState("");

  const fetchData = async (announcementId) => {
    setLoading(true);
    try {
      const searchParams = announcementId
        ? JSON.stringify([
            {
              columnName: "AnnouncementID",
              value: announcementId,
              operator: "=",
              dataType: "varchar(MAX)",
              logicOperator: "AND",
            },
          ])
        : "[]";

      const payload = {
        tableName: "announcement-status-tracker",
        searchParams,
        columns: "*",
        location: "pmis-global",
        pageNumber: 1,
        pageSize: 10000,
        sortData: " StageOrder asc",
        additionalWhere: "",
        qID: 0,
      };
      const response = await advanceSearch(payload);
      setData(response?.results || []);
    } catch (error) {
      ShowError(
        getFormalApiErrorMessage(
          error,
          "We are unable to retrieve the announcement status data at this time. Please try again shortly."
        )
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(appliedAnnouncementId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedAnnouncementId]);

  const handleSearch = () => {
    setAppliedAnnouncementId(announcementIdInput.trim());
  };

  const handleClear = () => {
    setAnnouncementIdInput("");
    setAppliedAnnouncementId("");
  };

  // Group the flat rows by AnnouncementID so each announcement renders as
  // one card, and within each announcement group again by Stage so the
  // pipeline reads as labeled sections instead of one long mixed list.
  const groupedByAnnouncement = useMemo(
    () =>
      data.reduce((groups, row) => {
        const key = row.AnnouncementID?.trim() ?? "Unknown";
        if (!groups[key]) groups[key] = [];
        groups[key].push(row);
        return groups;
      }, {}),
    [data]
  );

  // Announcement cards themselves ordered by their most recent activity too
  // (whichever record inside them was touched last), not just the tree
  // inside each card - otherwise the outer list order would still be
  // whatever order the API happened to return rows in.
  const sortedAnnouncementEntries = useMemo(() => {
    const mostRecentTime = (rows) =>
      rows.reduce((latest, r) => {
        const t = r.LatestPerformedOn ? new Date(r.LatestPerformedOn).getTime() : -Infinity;
        return t > latest ? t : latest;
      }, -Infinity);

    return Object.entries(groupedByAnnouncement).sort(
      ([, rowsA], [, rowsB]) => mostRecentTime(rowsB) - mostRecentTime(rowsA)
    );
  }, [groupedByAnnouncement]);

  const groupByStage = (rows) => {
    const groups = rows.reduce((acc, row) => {
      if (!acc[row.Stage]) acc[row.Stage] = [];
      acc[row.Stage].push(row);
      return acc;
    }, {});
    return STAGE_ORDER.filter((stage) => groups[stage]?.length).map((stage) => ({
      stage,
      rows: groups[stage],
    }));
  };

  const hasNoActivity = (row) =>
    !row.LatestRemark && !row.LatestPerformedBy && !row.LatestPerformedOn;

  // Most recent LatestPerformedOn first; rows with no logged activity at
  // all have nothing to be "recent" about, so they sort to the bottom.
  const latestFirst = (a, b) => {
    const aTime = a.LatestPerformedOn ? new Date(a.LatestPerformedOn).getTime() : -Infinity;
    const bTime = b.LatestPerformedOn ? new Date(b.LatestPerformedOn).getTime() : -Infinity;
    return bTime - aTime;
  };
  const sortRowsByLatest = (rows) => [...rows].sort(latestFirst);
  // Same ordering applied to a tree of {row, children} nodes, recursively,
  // so each level of the Eligibility -> General Availability ->
  // Certification chain independently shows its most recently active
  // entries first.
  const sortNodesByLatest = (nodes) =>
    [...nodes]
      .sort((a, b) => latestFirst(a.row, b.row))
      .map((node) => ({ ...node, children: sortNodesByLatest(node.children) }));

  // Build the actual parent -> child chain for one announcement's rows,
  // using the lineage columns from the SQL function (EligibilityID /
  // GeneralAvailabilityID / CertificationID), instead of three separate
  // flat lists that leave the reader to cross-reference IDs by hand.
  // Anything that can't be traced to a parent (shouldn't normally happen,
  // but the underlying tables aren't perfectly clean) is still shown, just
  // under its own "Unlinked" bucket, so nothing silently disappears.
  const buildTree = (rows) => {
    const evaluationRows = rows.filter((r) => r.Stage === "Evaluation");
    const eligibilityRows = rows.filter((r) => r.Stage === "Eligibility");
    const gaRows = rows.filter((r) => r.Stage === "General Availability");
    const certRows = rows.filter((r) => r.Stage === "Certification");

    const gaEntityIds = new Set(gaRows.map((g) => g.EntityID));
    const eligibilityEntityIds = new Set(eligibilityRows.map((e) => e.EntityID));

    const certsForGa = (gaEntityId) => certRows.filter((c) => c.GeneralAvailabilityID === gaEntityId);
    const gasForEligibility = (eligibilityEntityId) =>
      gaRows.filter((g) => g.EligibilityID === eligibilityEntityId);

    const eligibilityTree = eligibilityRows.map((elig) => ({
      row: elig,
      children: gasForEligibility(elig.EntityID).map((ga) => ({
        row: ga,
        children: certsForGa(ga.EntityID).map((cert) => ({ row: cert, children: [] })),
      })),
    }));

    const unlinkedGa = gaRows
      .filter((g) => !g.EligibilityID || !eligibilityEntityIds.has(g.EligibilityID))
      .map((ga) => ({
        row: ga,
        children: certsForGa(ga.EntityID).map((cert) => ({ row: cert, children: [] })),
      }));

    const unlinkedCert = certRows.filter(
      (c) => !c.GeneralAvailabilityID || !gaEntityIds.has(c.GeneralAvailabilityID)
    );

    return {
      evaluationRows: sortRowsByLatest(evaluationRows),
      eligibilityTree: sortNodesByLatest(eligibilityTree),
      unlinkedGa: sortNodesByLatest(unlinkedGa),
      unlinkedCert: sortRowsByLatest(unlinkedCert),
    };
  };

  // One row + its nested children, indented and color-coded by stage so
  // the parent -> child chain (Eligibility -> General Availability ->
  // Certification) is visible at a glance instead of needing to compare ID
  // columns across separate tables.
  const TreeNode = ({ node, depth = 0 }) => {
    const { row, children } = node;
    return (
      <Accordion
        defaultExpanded
        elevation={0}
        disableGutters
        sx={{
          border: "1px solid #e0e0e0",
          borderLeft: `4px solid ${STAGE_COLOR[row.Stage] || "#6c757d"}`,
          mb: 1,
          ml: depth * 2,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Chip
              size="small"
              label={row.Stage}
              sx={{ color: "white", background: STAGE_COLOR[row.Stage] || "#6c757d" }}
            />
            <b>{renderValue(row.EntityID)}</b>
            <Chip
              size="small"
              label={renderValue(row.EntityStatus)}
              sx={{ color: "white", background: getStatusColor(row.EntityStatus) }}
            />
            {row.DaysPending !== null && row.DaysPending !== undefined && (
              <Chip
                size="small"
                label={`${row.DaysPending}d`}
                sx={{ color: "white", background: getAgingColor(row.DaysPending) }}
              />
            )}
            {row.ResponsibleParty && (
              <Typography variant="caption" color="text.secondary">
                &#128100; {row.ResponsibleParty}
              </Typography>
            )}
            {children.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                → {children.length} downstream
              </Typography>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails>
          {row.Stage === "Certification" && row.CertificationStepIndex !== null && row.CertificationStepIndex !== undefined ? (
            <div className="mb-2">
              <CertificationStepper stepIndex={row.CertificationStepIndex} />
            </div>
          ) : row.RemarksTrail ? (
            <div className="mb-2">
              <RemarksTrail trail={row.RemarksTrail} />
            </div>
          ) : hasNoActivity(row) ? (
            <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary", mb: children.length ? 1 : 0 }}>
              No activity logged yet
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ mb: children.length ? 1 : 0 }}>
              {renderValue(row.LatestRemark)}
              {row.LatestPerformedOn ? ` — ${new Date(row.LatestPerformedOn).toLocaleString()}` : ""}
            </Typography>
          )}
          {children.map((child, idx) => (
            <TreeNode key={idx} node={child} depth={depth + 1} />
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  // Stage-to-stage flow, for the Sankey chart below. Evaluation has no
  // traceable link forward (no shared ID with Eligibility), so that hop is
  // shown as overall volume. Eligibility -> General Availability and
  // General Availability -> Certification use the real lineage columns, so
  // those counts only include records actually traceable to the prior stage.
  const sankeyOptions = useMemo(() => {
    const byStage = STAGE_ORDER.reduce((acc, stage) => {
      acc[stage] = data.filter((r) => r.Stage === stage);
      return acc;
    }, {});

    const links = [];
    if (byStage.Evaluation.length && byStage.Eligibility.length) {
      links.push(["Evaluation", "Eligibility", byStage.Eligibility.length]);
    }
    const gaWithEligibility = byStage["General Availability"].filter((r) => r.EligibilityID).length;
    if (gaWithEligibility) {
      links.push(["Eligibility", "General Availability", gaWithEligibility]);
    }
    const certWithGa = byStage.Certification.filter((r) => r.GeneralAvailabilityID).length;
    if (certWithGa) {
      links.push(["General Availability", "Certification", certWithGa]);
    }

    return {
      chart: { height: 260 },
      title: { text: "" },
      credits: { enabled: false },
      series: [
        {
          type: "sankey",
          name: "Records",
          keys: ["from", "to", "weight"],
          data: links,
          nodes: STAGE_ORDER.map((stage) => ({
            id: stage,
            color: STAGE_COLOR[stage],
          })),
          dataLabels: { style: { fontSize: "11px" } },
        },
      ],
    };
  }, [data]);

  // Across everything currently loaded, the records that have gone the
  // longest without activity - i.e. who is sitting on what right now.
  const bottlenecks = useMemo(
    () =>
      [...data]
        .filter((r) => r.DaysPending !== null && r.DaysPending !== undefined)
        .sort((a, b) => (b.DaysPending ?? 0) - (a.DaysPending ?? 0))
        .slice(0, 10),
    [data]
  );

  return (
    <ErrorBoundary>
      <Fragment>
        <Container fluid={true}>
          <Row className="bg-primary rounded py-2 mb-3 sticky-top">
            <Col className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faArrowTrendUp} size="xl" color="white" />
              <Typography variant="h6" style={{ color: "white" }}>
                Announcement Status Dashboard
              </Typography>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs="12" className="d-flex gap-2 align-items-center flex-wrap">
              <TextField
                size="small"
                label="Announcement ID"
                placeholder="e.g. DOA-26-101"
                value={announcementIdInput}
                onChange={(e) => setAnnouncementIdInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                onClick={handleSearch}
              >
                Search
              </Button>
              <Button variant="outlined" size="small" onClick={handleClear}>
                Show All
              </Button>
            </Col>
          </Row>

          {loading ? (
            <Row>
              {[1, 2, 3].map((_, i) => (
                <Col xs="12" key={i} className="p-2">
                  <Card>
                    <CardHeader title={<Skeleton variant="text" width="40%" />} />
                    <CardContent>
                      <Skeleton variant="rectangular" height={120} />
                    </CardContent>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : data.length === 0 ? (
            <Row>
              <Col xs="12">
                <Typography variant="body1" className="p-3 text-center">
                  No announcement status data found.
                </Typography>
              </Col>
            </Row>
          ) : (
            <Fragment>
              <Row className="mb-3">
                <Col xs="12" md="6" className="p-2">
                  <Card style={{ height: "100%" }}>
                    <CardHeader title="Recruitment Pipeline Flow" />
                    <CardContent>
                      <HighchartsReact highcharts={Highcharts} options={sankeyOptions} />
                    </CardContent>
                  </Card>
                </Col>
                <Col xs="12" md="6" className="p-2">
                  <Card style={{ height: "100%" }}>
                    <CardHeader title="Longest Pending - Who's Sitting On What" />
                    <CardContent style={{ maxHeight: 300, overflowY: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Stage</TableCell>
                            <TableCell>Announcement</TableCell>
                            <TableCell>Responsible</TableCell>
                            <TableCell>Days Pending</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {bottlenecks.map((row, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={row.Stage}
                                  sx={{ color: "white", background: STAGE_COLOR[row.Stage] || "#6c757d" }}
                                />
                              </TableCell>
                              <TableCell>{renderValue(row.AnnouncementID)}</TableCell>
                              <TableCell>{renderValue(row.ResponsibleParty)}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={`${row.DaysPending}d`}
                                  sx={{ color: "white", background: getAgingColor(row.DaysPending) }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </Col>
              </Row>

              <Row>
                {sortedAnnouncementEntries.map(([announcementId, rows]) => {
                  const first = rows[0];
                  const stageGroups = groupByStage(rows);
                  const { evaluationRows, eligibilityTree, unlinkedGa, unlinkedCert } = buildTree(rows);
                  return (
                    <Col xs="12" key={announcementId} className="p-2">
                      <Card>
                        <CardHeader
                          title={
                            <div>
                              <Typography variant="h6" className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon
                                  color="var(--theme-default)"
                                  icon={faBullhorn}
                                  size="lg"
                                />
                                Announcement: <u>{renderValue(announcementId)}</u>
                              </Typography>
                              <Typography variant="body2" className="mb-2">
                                HR Control: {renderValue(first?.HrControl)} &nbsp;|&nbsp;
                                Position: {renderValue(first?.PositionNo)} - {renderValue(first?.PositionTitle)}
                              </Typography>
                              {/* At-a-glance summary: how many records are sitting in each
                                  stage, so you don't have to scroll the tree below to see
                                  where this announcement stands overall. */}
                              <div className="d-flex gap-2 flex-wrap">
                                {stageGroups.map(({ stage, rows: stageRows }) => (
                                  <Chip
                                    key={stage}
                                    size="small"
                                    label={`${stage}: ${stageRows.length}`}
                                    sx={{
                                      color: "white",
                                      background: STAGE_COLOR[stage] || "#6c757d",
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          }
                        />
                        <CardContent>
                          {/* Evaluation has no traceable link to a specific Eligibility
                              record, so it's shown as its own flat list rather than nested. */}
                          {evaluationRows.length > 0 && (
                            <Fragment>
                              <Typography variant="overline" color="text.secondary">
                                Evaluation (no linked Eligibility record yet)
                              </Typography>
                              {evaluationRows.map((row, idx) => (
                                <TreeNode key={idx} node={{ row, children: [] }} />
                              ))}
                            </Fragment>
                          )}

                          {/* The real chain: each Eligibility record, with its own General
                              Availability children nested underneath, and each of those
                              with its own Certification children nested underneath that -
                              so which GA belongs to which Eligibility, and which
                              Certification belongs to which GA, is visible directly from
                              the indentation instead of needing to compare ID columns. */}
                          {eligibilityTree.map((node, idx) => (
                            <TreeNode key={idx} node={node} />
                          ))}

                          {unlinkedGa.length > 0 && (
                            <Fragment>
                              <Typography variant="overline" color="text.secondary">
                                General Availability records not linked to an Eligibility record above
                              </Typography>
                              {unlinkedGa.map((node, idx) => (
                                <TreeNode key={idx} node={node} />
                              ))}
                            </Fragment>
                          )}

                          {unlinkedCert.length > 0 && (
                            <Fragment>
                              <Typography variant="overline" color="text.secondary">
                                Certification records not linked to a General Availability record above
                              </Typography>
                              {unlinkedCert.map((row, idx) => (
                                <TreeNode key={idx} node={{ row, children: [] }} />
                              ))}
                            </Fragment>
                          )}
                        </CardContent>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Fragment>
          )}
        </Container>
      </Fragment>
    </ErrorBoundary>
  );
}

export default AnnouncementStatusDashboard;
